import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import {
  FiShoppingCart,
  FiSearch,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiCalendar,
  FiArrowLeft,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import '../styles/admin.css';

const PAGE_SIZE = 25;

export default function AllOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [visibleCount, setVisibleCount] = useState(50);
  // Refund state comes from Firestore (each order's own refundedItems/fullyRefunded
  // fields, written by the backend's /refund-payment route) - a per-browser
  // localStorage cache here would show wrong status on any other browser/device.
  const [refundedItems, setRefundedItems] = useState({});
  const [refundedOrders, setRefundedOrders] = useState({});

  const getOrderStatus = (order) => {
    const isFullyRefunded = refundedOrders[order.orderReference];
    const isCaptured = order.captureStatus === 'CAPTURED';
    const orderItems = order.items || order.products || [];
    let hasPartialRefunds = false;
    let totalRefundedAmount = 0;
    orderItems.forEach(item => {
      const key = `${order.orderReference}_${item.name}`;
      const refundedQty = refundedItems[key] || 0;
      if (refundedQty > 0 && refundedQty < (item.quantity || 1)) hasPartialRefunds = true;
      totalRefundedAmount += refundedQty * (item.price || 0);
    });
    if (isFullyRefunded) return 'fully-refunded';
    if (hasPartialRefunds || totalRefundedAmount > 0) return 'partially-refunded';
    if (isCaptured) return 'captured-paid';
    return 'pending-authorized';
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'captured-paid': return { label: 'Betalt' };
      case 'fully-refunded': return { label: 'Refundert' };
      case 'partially-refunded': return { label: 'Del-refundert' };
      case 'pending-authorized': default: return { label: 'Kansellert' };
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: orderRows, error: ordersError } = await supabase
        .from('orders')
        .select('*, users(name, email), order_items(*)')
        .order('date_purchased', { ascending: false });
      if (ordersError) throw ordersError;

      const allOrders = (orderRows || [])
        .filter(o => o.order_reference)
        .map(o => ({
          id: o.id,
          userId: o.user_id,
          userName: o.users?.name || 'Unknown',
          userEmail: o.users?.email || 'No email',
          orderReference: o.order_reference,
          buyerName: o.buyer_name,
          phoneNumber: o.phone_number,
          datePurchased: o.date_purchased,
          totalPrice: o.total_price,
          captureStatus: o.capture_status,
          discountCode: o.discount_code,
          discountAmount: o.discount_amount,
          fullyRefunded: o.fully_refunded,
          refundedItems: o.refunded_items,
          items: (o.order_items || []).map(i => ({
            name: i.name, price: i.price, quantity: i.quantity, category: i.category,
          })),
        }));
      setOrders(allOrders);

      const refundedItemsFromDB = {};
      const refundedOrdersFromDB = {};
      allOrders.forEach(order => {
        if (order.refundedItems) {
          Object.assign(refundedItemsFromDB, order.refundedItems);
        }
        if (order.fullyRefunded) {
          refundedOrdersFromDB[order.orderReference] = true;
        }
      });
      setRefundedItems(refundedItemsFromDB);
      setRefundedOrders(refundedOrdersFromDB);
    } catch (err) {
      setError('Klarte ikke å hente data');
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderReference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date-desc': return new Date(b.datePurchased || 0) - new Date(a.datePurchased || 0);
      case 'date-asc': return new Date(a.datePurchased || 0) - new Date(b.datePurchased || 0);
      case 'name-asc': return (a.buyerName || a.userName || '').localeCompare(b.buyerName || b.userName || '');
      case 'name-desc': return (b.buyerName || b.userName || '').localeCompare(a.buyerName || a.userName || '');
      case 'amount-desc': return (b.totalPrice || 0) - (a.totalPrice || 0);
      case 'amount-asc': return (a.totalPrice || 0) - (b.totalPrice || 0);
      case 'status': return getOrderStatus(a).localeCompare(getOrderStatus(b));
      default: return 0;
    }
  });

  // Reset visible count when search/sort changes
  useEffect(() => {
    setVisibleCount(50);
  }, [searchTerm, sortBy]);

  if (loading) {
    return (
      <div className="admin-loading">
        <FiRefreshCw className="loading-spinner" />
        <p>Laster bestillinger...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <p>Feil: {error}</p>
        <button onClick={fetchData}>Prøv igjen</button>
      </div>
    );
  }

  const visibleOrders = filteredOrders.slice(0, visibleCount);
  const hasMore = visibleCount < filteredOrders.length;

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-dashboard">
        <div className="admin-header">
          <div>
            <h1>
              <FiShoppingCart style={{ marginRight: '12px', verticalAlign: 'middle' }} />
              Alle bestillinger
            </h1>
            <p>Oversikt over alle kundebestillinger</p>
          </div>
          <div className="admin-actions">
            <button className="refresh-btn" onClick={() => navigate('/')}>
              <FiArrowLeft />
              Tilbake
            </button>
            <button className="refresh-btn" onClick={fetchData}>
              <FiRefreshCw />
              Oppdater
            </button>
          </div>
        </div>

        <div className="card orders-card">
          <div className="orders-header">
            <h2><FiShoppingCart style={{ marginRight: '8px', verticalAlign: 'middle' }} />Alle bestillinger</h2>
            <span className="orders-count">{filteredOrders.length} bestillinger</span>
          </div>
          <div className="orders-filter-card">
            <div className="orders-toolbar">
              <div className="search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Søk bestillinger, navn, e-post..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="orders-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date-desc">Nyeste først</option>
                <option value="date-asc">Eldste først</option>
                <option value="name-asc">Navn A–Å</option>
                <option value="name-desc">Navn Å–A</option>
                <option value="amount-desc">Beløp høy–lav</option>
                <option value="amount-asc">Beløp lav–høy</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Bestilling</th>
                  <th>Kunde</th>
                  <th>Beløp</th>
                  <th>Status</th>
                  <th>Dato</th>
                  <th>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      <p>Ingen bestillinger funnet</p>
                    </td>
                  </tr>
                ) : (
                  visibleOrders.map(order => {
                    const status = getOrderStatus(order);
                    const statusInfo = getStatusInfo(status);
                    return (
                      <tr key={order.id} className="order-row">
                        <td>
                          <div className="order-id-cell">
                            <strong>OR-{order.orderReference?.substring(0, 8)}</strong>
                          </div>
                        </td>
                        <td>
                          <div className="customer-info">
                            <div className="customer-name">{order.buyerName || order.userName}</div>
                            <div className="customer-email">{order.userEmail}</div>
                            {order.phoneNumber && (
                              <div className="customer-phone">{order.phoneNumber}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="amount-cell">{order.totalPrice?.toFixed(0)} NOK</span>
                        </td>
                        <td>
                          <div className="status-cell">
                            <span className={`status-badge status-${status}`}>
                              {status === 'captured-paid' && <FiCheckCircle style={{ fontSize: '14px' }} />}
                              {status === 'fully-refunded' && <FiXCircle style={{ fontSize: '14px' }} />}
                              {status === 'partially-refunded' && <FiXCircle style={{ fontSize: '14px' }} />}
                              {status === 'pending-authorized' && <FiClock style={{ fontSize: '14px' }} />}
                              {statusInfo.label}
                            </span>
                            <span className="capture-status-text">{order.captureStatus || 'PENDING'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="date-cell">
                            <div className="date-main">
                              <FiCalendar style={{ fontSize: '14px', marginRight: '4px' }} />
                              {order.datePurchased ? new Date(order.datePurchased).toLocaleDateString('no-NO', {
                                day: '2-digit', month: 'short', year: 'numeric'
                              }) : 'N/A'}
                            </div>
                            <div className="date-time">
                              {order.datePurchased ? new Date(order.datePurchased).toLocaleTimeString('no-NO', {
                                hour: '2-digit', minute: '2-digit'
                              }) : ''}
                            </div>
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary view-order-btn"
                            title="Se kundebestillinger"
                            onClick={() => window.open(`/customer-orders/${order.userId}`, '_blank')}
                          >
                            <FiShoppingCart style={{ fontSize: '16px' }} />
                            Se
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="load-more-row">
              <span className="load-more-info">
                Viser {visibleCount} av {filteredOrders.length} bestillinger
              </span>
              <button
                className="load-more-btn"
                onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
              >
                Last inn {Math.min(PAGE_SIZE, filteredOrders.length - visibleCount)} til
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
